import { FormikProvider, useFormik } from "formik";
import React, { useMemo } from "react";

import {
    AccountClass,
    AccountSubtype,
    AccountType,
} from "@common/constants/domains/accounting/accounts";
import { createAccountBodySchema } from "@common/schemas/domains/accounting/account.schema";
import {
    Account,
    AccountGroup,
} from "@common/types/domains/accounting/account";
import { AccountCodeNameInput } from "@components/accounting/chartOfAccounts/AccountCodeNameInput";
import { BusinessEntitiesSectionedCheckboxTable } from "@components/accounting/chartOfAccounts/BusinessEntitiesSectionedCheckboxTable";
import { validateFormikWithZodSchema } from "@helpers/form";
import { useEntities } from "@hooks/useEntities";
import { Button } from "@design-system/Button/Button";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import {
    useCreateAccountMutation,
    useWorkspaceAccounts,
} from "@api/account.api";
import { useChartOfAccountsQuery } from "@api/accountingReports.api";
import { AccountTypeSelectField } from "@components/forms/AccountTypeSelectField/AccountTypeSelectField";
import { GroupSelectField } from "@components/forms/GroupSelectField/GroupSelectField";
import { HStack } from "@design-system/HStack/HStack";
import { Field } from "@design-system/Field/Field";
import { Input } from "@design-system/Input/Input";
import styles from "../styles.module.scss";
import {
    buildCreateAccountBody,
    buildCreateAccountInitialValues,
    CoAAccountFormValues,
    getCreateAccountErrorMessage,
    getCreateAccountGroupFieldState,
    useCoAAccountCreateFormHelpers,
} from "./useCoAAccountCreateFormHelpers";

interface Props {
    entityIds?: number[];
    hideEntitySelection?: boolean;
    accountName: string;
    accountType: AccountType;
    accountSubtype: AccountSubtype;
    parentGroupId?: string | null;
    availableClassifications?: AccountClass[];
    onAccountsCreated?: (accounts: Account[]) => void;
    onCancel: () => void;
}

export const CoAAccountCreateForm: React.FC<Props> = ({
    entityIds,
    hideEntitySelection,
    accountName,
    accountType: type,
    accountSubtype: subtype,
    parentGroupId,
    availableClassifications,
    onAccountsCreated,
    onCancel,
}) => {
    const businessEntities = useEntities({
        onlyBusiness: true,
    });

    const { error: toastError } = useToaster();
    const createAccountMutation = useCreateAccountMutation();
    const allAccounts = useWorkspaceAccounts();
    const { data: chartOfAccountsData } = useChartOfAccountsQuery();
    const groups = useMemo<AccountGroup[]>(
        () => chartOfAccountsData?.groups ?? [],
        [chartOfAccountsData?.groups],
    );
    const validatedInitialValues = useMemo(
        () =>
            buildCreateAccountInitialValues({
                accountName,
                type,
                subtype,
                availableClassifications,
                entityIds,
                businessEntities,
                parentGroupId,
            }),
        [
            accountName,
            type,
            subtype,
            availableClassifications,
            entityIds,
            businessEntities,
            parentGroupId,
        ],
    );

    const formik = useFormik<CoAAccountFormValues>({
        initialValues: validatedInitialValues,
        enableReinitialize: true,
        validate: (values) =>
            validateFormikWithZodSchema(createAccountBodySchema)(
                buildCreateAccountBody(values, { groups }),
            ),
        onSubmit: async (values) => {
            try {
                const data = await createAccountMutation.mutateAsync(
                    buildCreateAccountBody(values, { groups }),
                );
                onAccountsCreated?.(data.accounts);
                onCancel();
            } catch (error) {
                toastError(getCreateAccountErrorMessage(error));
            }
        },
    });

    const selectedEntities = useMemo(
        () =>
            businessEntities.filter((businessEntity) =>
                formik.values.entityIds.includes(businessEntity.id),
            ),
        [businessEntities, formik.values.entityIds],
    );

    const groupFieldState = useMemo(
        () =>
            getCreateAccountGroupFieldState({
                groups,
                entityIds: formik.values.entityIds,
                parentGroupId: formik.values.parentGroupId,
                type: formik.values.type,
            }),
        [
            groups,
            formik.values.entityIds,
            formik.values.parentGroupId,
            formik.values.type,
        ],
    );

    const {
        nameInputRef,
        messages,
        availableAccountTypes,
        changeAccountType,
        changeAccountCodeText,
    } = useCoAAccountCreateFormHelpers({
        selectedEntities,
        accounts: allAccounts.data ?? [],
        formik,
        availableClassifications,
        effectiveParentGroupId: groupFieldState.parentGroupId,
    });

    // Show classification as a disabled field if there's exactly one
    const singleClassification =
        availableClassifications?.length === 1
            ? availableClassifications[0]
            : null;

    return (
        <FormikProvider value={formik}>
            <form onSubmit={formik.handleSubmit}>
                {singleClassification && (
                    <Field
                        label="Classification"
                        className={styles.classificationField}
                    >
                        <Input
                            size="xl"
                            value={singleClassification}
                            disabled
                        />
                    </Field>
                )}
                <AccountTypeSelectField
                    name="type"
                    label="Type"
                    placeholder="Select a type"
                    value={formik.values.type}
                    types={availableAccountTypes}
                    onChange={changeAccountType}
                />
                <GroupSelectField
                    parentGroupFieldName="parentGroupId"
                    label="Group"
                    placeholder="Select a group"
                    disabled={groupFieldState.isDisabled}
                    groups={groupFieldState.groups}
                    type={formik.values.type}
                    value={groupFieldState.parentGroupId}
                    onChange={(selectedParentGroupId) =>
                        formik.setFieldValue(
                            "parentGroupId",
                            selectedParentGroupId,
                        )
                    }
                />

                <AccountCodeNameInput
                    className={styles.nameFields}
                    size="xl"
                    codeText={formik.values.codeText ?? ""}
                    name={formik.values.name}
                    namePlaceholder="Enter account name"
                    nameInputRef={nameInputRef}
                    onCodeTextChange={changeAccountCodeText}
                    onNameChange={(name) => formik.setFieldValue("name", name)}
                />

                {businessEntities.length > 1 && !hideEntitySelection ? (
                    <BusinessEntitiesSectionedCheckboxTable
                        businessEntities={businessEntities}
                        messages={messages}
                        value={selectedEntities.map(
                            (selectedEntity) => selectedEntity.id,
                        )}
                        onChange={(values) => {
                            formik.setFieldValue("entityIds", values);
                        }}
                        searchable
                    />
                ) : (
                    messages.length > 0 && (
                        <div
                            className={styles.errorMessage}
                            data-testid="error-message"
                        >
                            {messages[0].message}
                        </div>
                    )
                )}

                <HStack
                    as="footer"
                    gap={12}
                    justify="end"
                    className={styles.footer}
                >
                    <Button
                        type="button" // necessary to prevent form submission counting this as "submit" and creating the account
                        variant="secondary"
                        onClick={() => onCancel()}
                        size="xl"
                        data-testid="confirmation-modal-no-button"
                        disabled={formik.isSubmitting}
                    >
                        Cancel
                    </Button>

                    <ButtonWithLoader
                        loading={formik.isSubmitting}
                        variant="primary"
                        type="submit"
                        size="xl"
                        data-testid="confirmation-modal-yes-button"
                        hotKey="mod+enter"
                        hotKeyOptions={{
                            description: "Create account",
                            preventDefault: true,
                            enableOnFormTags: true,
                        }}
                        disabled={
                            formik.values.name.trim().length === 0 ||
                            !!messages.length ||
                            formik.isSubmitting ||
                            formik.values.entityIds.length === 0
                        }
                    >
                        Create
                    </ButtonWithLoader>
                </HStack>
            </form>
        </FormikProvider>
    );
};
